import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IUseOrganizationUpdateUserMutationValues } from '~/lib/api/modules/Organizations/hooks/useOrganizationUpdateUserMutation';
import type { IApiGetUserResponseBody } from '~/lib/api/modules/Users';
import type {
  IOrganizationUserFormProps,
  IOrganizationUserFormValues,
} from './OrganizationUserForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useOrganizationUpdateUserMutation } from '~/lib/api/modules/Organizations/hooks/useOrganizationUpdateUserMutation';
import { useOrganizationUserQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationUserQuery';
import { OrganizationUserForm } from './OrganizationUserForm';

export type IUpdateOrganizationUserFormProps = Omit<
  IOrganizationUserFormProps,
  'mode'
> & {
  onSuccess?: (
    outputData: IApiGetUserResponseBody,
    inputValues: IUseOrganizationUpdateUserMutationValues,
  ) => void;
  cancelUrl?: string;
  organizationId: string;
  userId: string;
};

export const UpdateOrganizationUserForm: React.FC<
  IUpdateOrganizationUserFormProps
> = (props) => {
  const { sx, onSuccess, cancelUrl, organizationId, userId } = props;
  const { t } = useTranslation();
  const organizationUserQuery = useOrganizationUserQuery({
    organizationId,
    userId,
  });
  const organizationUpdateUserMutation = useOrganizationUpdateUserMutation();
  const organizationUser = organizationUserQuery.data;

  const handleSubmit: SubmitHandler<IOrganizationUserFormValues> = (values) => {
    if (organizationUpdateUserMutation.isLoading) {
      return;
    }

    return organizationUpdateUserMutation.mutateAsync(
      {
        organizationId,
        userId,
        body: {
          roles: values.role ? [values.role] : [],
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>
        {t('UpdateOrganizationUserForm.title')}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={organizationUserQuery.isLoading}
        error={organizationUserQuery.error}
      >
        {organizationUser ? (
          <OrganizationUserForm
            mode="edition"
            cancelUrl={cancelUrl}
            defaultValues={{
              role: organizationUser.roles?.[0],
            }}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
